import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, Phone, MapPin, Compass, Sparkles } from "lucide-react";

// Section-aware exit/scroll-triggered lead capture popup
const messagesByPath = (pathname) => {
  if (pathname.startsWith("/holiday-homes"))
    return {
      icon: MapPin,
      kicker: "Holiday Homes",
      title: "Need more guidance on holiday homes?",
      sub: "Get a free 45-minute call with Zentry. Goa or Uttarakhand — we'll walk through what fits your goals, budget, and timeline.",
    };
  if (pathname.startsWith("/plots"))
    return {
      icon: Compass,
      kicker: "Plots & Land",
      title: "Considering a plot in a growth corridor?",
      sub: "Don't decide on a brochure. Get a structured 45-minute call to evaluate the corridor, the developer, and your real exit.",
    };
  if (pathname.startsWith("/insights"))
    return {
      icon: Sparkles,
      kicker: "Apply this to your situation",
      title: "Reading is great. A conversation is better.",
      sub: "Book a Clarity Call and we'll apply this directly to your goal, budget, and the markets you're considering.",
    };
  if (pathname.startsWith("/zentry-build"))
    return {
      icon: Sparkles,
      kicker: "Zentry Build",
      title: "Thinking of building a holiday home?",
      sub: "We'll walk you through Zentry Build — land, design, construction, and Airbnb setup — coordinated end to end.",
    };
  return {
    icon: Phone,
    kicker: "Clarity before capital",
    title: "One call. No pressure. No follow-ups.",
    sub: "Tell us what you're considering. We'll tell you if it makes sense — even if that means not investing right now.",
  };
};

export default function ContextualPopup() {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setDismissed(false);
    setOpen(false);
    // Show after user has scrolled past 40% of the page OR 18s on page
    let shown = false;
    const trigger = () => {
      if (shown) return;
      const sessionDismissed = sessionStorage.getItem("zentry_popup_dismissed");
      if (sessionDismissed) return;
      // Don't show on clarity-call or contact pages
      if (pathname.startsWith("/clarity-call") || pathname.startsWith("/contact")) return;
      shown = true;
      setOpen(true);
    };
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.body.scrollHeight;
      if (total > 0 && scrolled / total > 0.45) trigger();
    };
    const t = setTimeout(trigger, 18000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  const dismiss = () => {
    setOpen(false);
    setDismissed(true);
    sessionStorage.setItem("zentry_popup_dismissed", "1");
  };

  if (!open || dismissed) return null;
  const m = messagesByPath(pathname);
  const Icon = m.icon;

  return (
    <div
      className="fixed bottom-20 sm:bottom-24 right-3 left-3 sm:left-auto sm:right-6 z-40 sm:w-[400px] bg-white border border-zinc-200 rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
      data-testid="contextual-popup"
    >
      <div className="bg-[#1D4ED8] h-1" />
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-widest text-[#1D4ED8] min-w-0">
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{m.kicker}</span>
          </div>
          <button
            onClick={dismiss}
            aria-label="close"
            data-testid="popup-close"
            className="text-zinc-400 hover:text-zinc-700 flex-shrink-0 -mt-1 -mr-1 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="font-display text-xl sm:text-2xl mt-3 text-zinc-900 leading-snug">{m.title}</div>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{m.sub}</p>
        <div className="mt-5 flex items-center gap-2">
          <Link
            to="/clarity-call"
            onClick={dismiss}
            className="btn-primary text-xs sm:text-sm flex-1 justify-center px-4 py-2.5"
            data-testid="popup-cta"
          >
            Book a Clarity Call
          </Link>
          <button
            onClick={dismiss}
            className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 px-2 whitespace-nowrap"
            data-testid="popup-later"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
